/**
 * Operation metadata for queries and mutators.
 *
 * The define helpers below wrap Zero's `defineQueryWithType` /
 * `defineMutatorWithType`. Besides the usual `(validator, fn)` form they take
 * `({ input, description?, destructive? }, fn)`. The metadata is kept on the
 * definition (as `meta`) next to Zero's `validator`, so every entry point
 * (REST spec, MCP, operation search, the executor's destructive gating) reads
 * it from one place.
 */
import { defineMutatorWithType, defineQueryWithType, } from "@rocicorp/zero";
import { z } from "zod";
function isSpec(value) {
    return (typeof value === "object" &&
        value !== null &&
        !("~standard" in value) &&
        "input" in value);
}
function splitSpec(spec) {
    const { input, ...meta } = spec;
    return { input, meta: meta };
}
function withSpecSupport(define) {
    return (first, fn) => {
        if (isSpec(first)) {
            const { input, meta } = splitSpec(first);
            return Object.assign(define(input, fn), { meta });
        }
        return fn === undefined ? define(first) : define(first, fn);
    };
}
export function defineMutatorWithMeta() {
    return withSpecSupport(defineMutatorWithType());
}
export function defineQueryWithMeta() {
    return withSpecSupport(defineQueryWithType());
}
/**
 * Validator for queries without args. Accepts `undefined` (how the web app
 * calls them) and `{}` (what REST sends).
 */
export const noArgsSchema = z.object({}).optional();
/** Reads the metadata of a query or mutator definition. */
export function getOperationMeta(definition) {
    const meta = definition?.meta;
    return meta ?? {};
}
/**
 * One-line descriptions of operation domains (the first segment of an
 * operation name, e.g. `people` in `people.update`). Each domain file
 * declares the domains it defines; `appDomainDescriptions` merges them.
 */
export function describeDomains(descriptions) {
    return descriptions;
}
